import { ArrowLeftIcon, Button, Flex } from '@iheartjane/reefer';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ORDERED_STEP_KEYS, WIZARD_STEPS } from '../../../utils/wizard/config';
import { WizardStepKey } from '../../../utils/wizard/types';
import type { WizardFormData, WizardProps } from '../../../utils/wizard/types';
import { StepIndicator } from '../../stepIndicator/stepIndicator';
import { CategoryStep } from '../categoryStep/categoryStep';
import { FeelingsStep } from '../feelingsStep/feelingsStep';
import styles from './wizardSelection.module.css';

interface WizardSelectionProps {
  form: WizardFormData;
  onComplete: () => void;
  updateForm: (form: WizardFormData) => void;
  wizardProps?: WizardProps;
}

export function WizardSelection({
  form,
  onComplete,
  updateForm,
  wizardProps,
}: WizardSelectionProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    topRef.current?.scrollIntoView({ block: 'start' });
  }, [currentStep]);

  const visibleSteps = useMemo(() => {
    const stepProps: WizardProps = {
      customProductTypeLabels: wizardProps?.customProductTypeLabels,
      disabledCategories: wizardProps?.disabledCategories ?? [],
      disabledFeelingTags: wizardProps?.disabledFeelingTags ?? [],
      storeState: wizardProps?.storeState,
    };

    return ORDERED_STEP_KEYS.filter((key) => {
      const step = WIZARD_STEPS[key];
      return !step.enabledForStore || step.enabledForStore(stepProps);
    });
  }, [
    wizardProps?.customProductTypeLabels,
    wizardProps?.disabledCategories,
    wizardProps?.disabledFeelingTags,
    wizardProps?.storeState,
  ]);

  const stepLabels = useMemo(
    () => visibleSteps.map((key) => WIZARD_STEPS[key].title),
    [visibleSteps]
  );

  const currentStepKey = visibleSteps[currentStep];
  const currentStepConfig = WIZARD_STEPS[currentStepKey];
  const FilterComponent = currentStepConfig.filterComponent;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === visibleSteps.length - 1;
  const isNextEnabled = form[currentStepKey].length > 0;

  const handleBack = useCallback(() => {
    setCurrentStep((step) => Math.max(0, step - 1));
  }, []);

  const handleNext = useCallback(() => {
    if (isLastStep) {
      onComplete();
    } else {
      setCurrentStep((step) => step + 1);
    }
  }, [isLastStep, onComplete]);

  return (
    <Flex
      flexDirection="column"
      justifyContent="space-between"
      grow={1}
      gap={12}
      className={styles['wizardBody']}
    >
      <StepIndicator ref={topRef} steps={stepLabels} current={currentStep} />
      <div className={styles['mobileDivider']} />

      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        grow={1}
      >
        {currentStepKey === WizardStepKey.Category ? (
          <CategoryStep
            form={form}
            updateForm={updateForm}
            customLabels={wizardProps?.customProductTypeLabels}
            disabledCategories={wizardProps?.disabledCategories}
          />
        ) : currentStepKey === WizardStepKey.Feelings ? (
          <FeelingsStep
            form={form}
            updateForm={updateForm}
            disabledFeelingTags={wizardProps?.disabledFeelingTags}
          />
        ) : (
          <FilterComponent form={form} updateForm={updateForm} />
        )}
      </Flex>

      <Flex
        justifyContent="center"
        gap={16}
        className={styles['wizardNavButtons']}
      >
        {!isFirstStep && (
          <Button
            variant="tertiary"
            label="Back"
            startIcon={<ArrowLeftIcon size="sm" mr={8} />}
            onClick={handleBack}
            className={styles['wizardNavButton']}
          />
        )}
        <Button
          variant="primary"
          label="Next"
          onClick={handleNext}
          disabled={!isNextEnabled}
          className={styles['wizardNavButton']}
        />
      </Flex>
    </Flex>
  );
}
